import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
  DialogTitle,
  DialogActions,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadIcon from "@mui/icons-material/UploadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import LoginIcon from "@mui/icons-material/Login";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import KeyIcon from "@mui/icons-material/KeyOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AddUserModal from "./AddUserModal";
import ManagedUserNotificationDialog from "./ManagedUserNotificationDialog";
import { BrandResetPasswordDialog } from "./wizard/BrandDialogs";
import { useStatus } from "../../popup/NotificationContext";
import { loginSuccess } from "../../store/authSlice";
import {
  adminApi,
  useAssignUserToGroupAdminMutation,
  useDeleteUserMutation,
  useEditDeviceMutation,
  useEditUserMutation,
  useGetAllDevicesQuery,
  useGetAllLiteDevicesQuery,
  useGetAllUsersQuery,
  useImpersonateUserMutation,
  useRevealUserPasswordMutation,
  useResetPasswordMutation,
  useRestoreUserMutation,
} from "../../redux/admin/adminApi";
import { downloadEntityTemplate, uploadEntityFile } from "../../services/importExportService";
import { getUserActionsEffective, hasUserPermission, normalizeRole, ROLES } from "../../utils/rbacHelper";
import {
  CUSTOM_ROWS_PER_PAGE_OPTIONS,
  getDisplayRange,
  getPaginatedRows,
  getRowsPerPageLabel,
  getTotalPagesForRows,
  ROWS_PER_PAGE_MENU_PROPS,
} from "../../utils/tablePagination";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const CUSTOMER_ROLES = new Set([ROLES.USER]);

const buildCsv = (rows) => {
  const headers = ["Name", "Password", "User ID", "Mobile", "Created By", "Last Login", "Last Activity", "Disabled", "Role"];
  const data = rows.map((row) =>
    [
      row.username,
      "••••••",
      row.uniqueId,
      row.mobileNo,
      row.createdByName,
      row.lastLoginLabel,
      row.lastActivityLabel,
      row.disabled ? "Yes" : "No",
      row.role,
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...data].join("\n");
};

const resolveCreatedByLabel = (user) => {
  const getLabel = (candidate) => {
    if (!candidate) return "";
    const username = String(candidate?.username || "").trim();
    const uniqueId = String(candidate?.uniqueId || "").trim();
    if (username && uniqueId) return `${username} (${uniqueId})`;
    return username || uniqueId || "";
  };

  return (
    String(user?.createdByName || "").trim() ||
    getLabel(user?.createdBy) ||
    getLabel(user?.dealerOwner) ||
    getLabel(user?.subAdminOwner) ||
    "-"
  );
};

const matchesManagedUserId = (candidate, userId) => {
  if (!userId || !candidate) return false;
  return String(candidate?._id || candidate?.id || candidate) === String(userId);
};

const isDeviceOwnedByUser = (device, userId) => {
  if (!userId || !device) return false;
  return [
    device?.assignedToUser,
    device?.adminId,
    device?.createdBy,
    device?.subAdminOwner,
    device?.dealerOwner,
    device?.dealer,
  ].some((candidate) => matchesManagedUserId(candidate, userId));
};

const formatActivityTimestamp = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const UserPortalModal = ({ open = true, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserRole = normalizeRole(currentUser?.role);
  const userActions = getUserActionsEffective(currentUser);
  const canAssignDevice = hasUserPermission(currentUser, "assignDevice");
  const canAssignUserToGA = hasUserPermission(currentUser, "assignUserToGA");
  const { showStatus } = useStatus();
  const fileInputRef = useRef(null);

  const { data: usersPayload = [], refetch } = useGetAllUsersQuery();
  const { data: devicesPayload = [] } = useGetAllLiteDevicesQuery(undefined, { skip: !open });
  const { data: fullDevicesPayload = [] } = useGetAllDevicesQuery(undefined, { skip: !open });
  const [impersonateUser] = useImpersonateUserMutation();
  const [editUser] = useEditUserMutation();
  const [editDevice] = useEditDeviceMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [restoreUser] = useRestoreUserMutation();
  const [assignUserToGroupAdmin] = useAssignUserToGroupAdminMutation();
  const [revealUserPassword] = useRevealUserPasswordMutation();
  const [resetPassword, { isLoading: isResettingPassword }] = useResetPasswordMutation();

  const [searchName, setSearchName] = useState("");
  const [searchLoginId, setSearchLoginId] = useState("");
  const [searchPhone, setSearchPhone] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [showAllCustomers, setShowAllCustomers] = useState(false);
  const [modalMode, setModalMode] = useState("table");
  const [editingUser, setEditingUser] = useState(null);
  const [resetDialogUser, setResetDialogUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmResetPassword, setConfirmResetPassword] = useState("");
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [loadingPasswordIds, setLoadingPasswordIds] = useState(() => new Set());
  const [assignDeviceDialogUser, setAssignDeviceDialogUser] = useState(null);
  const [notificationDialogUser, setNotificationDialogUser] = useState(null);
  const [assignDeviceSearch, setAssignDeviceSearch] = useState("");
  const [pendingAssignIds, setPendingAssignIds] = useState(() => new Set());
  const [assignOverrides, setAssignOverrides] = useState(() => new Map());
  const [assignDeviceCache, setAssignDeviceCache] = useState(() => new Map());
  const [assignGroupDialogUser, setAssignGroupDialogUser] = useState(null);
  const [assignGroupTargetId, setAssignGroupTargetId] = useState("");

  const normalizedUsers = useMemo(
    () =>
      (Array.isArray(usersPayload) ? usersPayload : [])
        .filter((user) => normalizeRole(user?.role) !== ROLES.SUPER_ADMIN)
        .map((user) => ({
          ...user,
          role: normalizeRole(user?.role),
          disabled: user?.isActive === false,
          uniqueId: user?.uniqueId || "-",
          mobileNo: user?.mobileNo || "-",
          createdByName: resolveCreatedByLabel(user),
          lastLoginLabel: formatActivityTimestamp(user?.lastLoginAt),
          lastActivityLabel: formatActivityTimestamp(
            user?.lastActivityAt || user?.updatedAt || user?.createdAt
          ),
        })),
    [usersPayload]
  );

  const filteredUsers = useMemo(() => {
    const nameQuery = searchName.trim().toLowerCase();
    const loginQuery = searchLoginId.trim().toLowerCase();
    const phoneQuery = searchPhone.trim();

    return normalizedUsers.filter((user) => {
      if (!showAllCustomers && !CUSTOMER_ROLES.has(user.role)) return false;
      if (nameQuery && !String(user?.username || "").toLowerCase().includes(nameQuery)) return false;
      if (loginQuery && !String(user?.uniqueId || "").toLowerCase().includes(loginQuery)) return false;
      if (phoneQuery && !String(user?.mobileNo || "").includes(phoneQuery)) return false;
      return true;
    });
  }, [normalizedUsers, searchName, searchLoginId, searchPhone, showAllCustomers]);

  const safeDevices = useMemo(
    () =>
      ((Array.isArray(devicesPayload) && devicesPayload.length
        ? devicesPayload
        : Array.isArray(fullDevicesPayload)
          ? fullDevicesPayload
          : []))
        .filter((device) => String(device?.status || "").toLowerCase() !== "deleted")
        .sort((left, right) => String(left?.name || "").localeCompare(String(right?.name || ""))),
    [devicesPayload, fullDevicesPayload]
  );

  useEffect(() => {
    const userId = String(assignDeviceDialogUser?._id || "");
    if (!userId) {
      setAssignDeviceCache(new Map());
      return;
    }

    setAssignDeviceCache((prev) => {
      const next = new Map(prev);
      safeDevices.forEach((device) => {
        const deviceId = String(device?._id || device?.id || "");
        if (!deviceId) return;
        next.set(deviceId, {
          ...(next.get(deviceId) || {}),
          ...device,
        });
      });
      return next;
    });
  }, [assignDeviceDialogUser?._id, safeDevices]);

  const selectedLicenseOwner = useMemo(() => {
    if (!assignDeviceDialogUser?._id) return null;

    const ownerCandidates = [
      assignDeviceDialogUser?.dealerOwner,
      assignDeviceDialogUser?.subAdminOwner,
      assignDeviceDialogUser?.createdBy,
    ]
      .filter(Boolean)
      .map((id) => String(id));

    const controllableRoles = new Set([ROLES.DEALER, ROLES.SUB_ADMIN, ROLES.MANAGER, ROLES.OFFICE_EMPLOYEE]);
    const normalizedAllUsers = Array.isArray(usersPayload) ? usersPayload : [];

    const matchedOwner = ownerCandidates
      .map((ownerId) => normalizedAllUsers.find((user) => String(user?._id || "") === ownerId))
      .find((user) => controllableRoles.has(normalizeRole(user?.role)));

    if (matchedOwner) return matchedOwner;
    if (controllableRoles.has(currentUserRole)) return currentUser;
    return null;
  }, [assignDeviceDialogUser, currentUser, currentUserRole, usersPayload]);

  const selectedLicenseCount = useMemo(() => {
    if (!selectedLicenseOwner) return 0;
    return Number(
      selectedLicenseOwner?.licenseSummary?.yearlyAvailableLicenses ??
      selectedLicenseOwner?.licenseSummary?.totalRemainingCredits ??
      selectedLicenseOwner?.remainingTokens ??
      selectedLicenseOwner?.credits?.balance ??
      0
    ) || 0;
  }, [selectedLicenseOwner]);

  const assignableDevices = useMemo(() => {
    const userId = String(assignDeviceDialogUser?._id || "");
    const query = String(assignDeviceSearch || "").trim().toLowerCase();

    const rows = Array.from(assignDeviceCache.values()).map((device) => {
      const deviceId = String(device?._id || device?.id || "");
      const assignedViewers = Array.isArray(device?.assignedToViewers) ? device.assignedToViewers : [];
      const isPrimaryAssigned = isDeviceOwnedByUser(device, userId);
      const isSharedAssigned = assignedViewers.some((viewer) => String(viewer?._id || viewer) === userId);
      const baseChecked = isPrimaryAssigned || isSharedAssigned;
      const overrideChecked = assignOverrides.get(deviceId);
      const checked = typeof overrideChecked === "boolean" ? overrideChecked : baseChecked;

      return {
        id: deviceId,
        name: device?.name || device?.vehicleNumber || device?.imei || "-",
        imei: device?.imei || "-",
        status: String(device?.liveData?.vehicleStatus || device?.status || "No_data"),
        lastUpdate: device?.lastDataReceivedAt
          ? new Date(device.lastDataReceivedAt).toLocaleString("en-GB", { hour12: true }).toUpperCase()
          : "",
        simNo: device?.msdn || "-",
        model: device?.model || "-",
        icon: device?.vehicleType || device?.deviceIcon || "Default",
        assignedToUserId: String(device?.assignedToUser?._id || device?.assignedToUser || ""),
        adminOwnerId: String(device?.adminId?._id || device?.adminId || ""),
        dealerOwnerId: String(device?.dealer?._id || device?.dealer || ""),
        assignedToViewers: assignedViewers.map((viewer) => String(viewer?._id || viewer)),
        checked,
      };
    });

    rows.sort((left, right) => {
      if (left.checked !== right.checked) return Number(right.checked) - Number(left.checked);
      return String(left.name || "").localeCompare(String(right.name || ""));
    });

    if (!query) return rows;

    return rows.filter((row) =>
      [row.name, row.imei, row.status, row.lastUpdate, row.simNo, row.model, row.icon]
        .some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [assignDeviceCache, assignDeviceDialogUser?._id, assignDeviceSearch, assignOverrides]);

  const assignDeviceDebug = useMemo(
    () => ({
      liteCount: Array.isArray(devicesPayload) ? devicesPayload.length : 0,
      fullCount: Array.isArray(fullDevicesPayload) ? fullDevicesPayload.length : 0,
      safeCount: Array.isArray(safeDevices) ? safeDevices.length : 0,
      cacheCount: assignDeviceCache.size,
      visibleCount: Array.isArray(assignableDevices) ? assignableDevices.length : 0,
      query: String(assignDeviceSearch || "").trim(),
      targetUserId: String(assignDeviceDialogUser?._id || ""),
    }),
    [assignDeviceCache.size, assignDeviceDialogUser?._id, assignDeviceSearch, assignableDevices, devicesPayload, fullDevicesPayload, safeDevices]
  );

  const groupAdmins = useMemo(
    () =>
      normalizedUsers.filter((user) => {
        if (currentUserRole === ROLES.SUPER_ADMIN || currentUserRole === ROLES.MANAGER) {
          return normalizeRole(user?.role) === ROLES.SUB_ADMIN || normalizeRole(user?.role) === ROLES.DEALER;
        }
        if (currentUserRole === ROLES.OFFICE_EMPLOYEE) {
          return normalizeRole(user?.role) === ROLES.SUB_ADMIN;
        }
        if (currentUserRole === ROLES.SUB_ADMIN) {
          return normalizeRole(user?.role) === ROLES.DEALER;
        }
        return false;
      }),
    [currentUserRole, normalizedUsers]
  );

  const totalPages = getTotalPagesForRows(filteredUsers.length, rowsPerPage);
  const pagedUsers = useMemo(() => getPaginatedRows(filteredUsers, page, rowsPerPage), [filteredUsers, page, rowsPerPage]);
  const displayRange = useMemo(() => getDisplayRange(filteredUsers.length, page, rowsPerPage), [filteredUsers.length, page, rowsPerPage]);
  const canRevealPasswords =
    currentUserRole === ROLES.SUPER_ADMIN ||
    currentUserRole === ROLES.SUB_ADMIN ||
    currentUserRole === ROLES.GROUP_ADMIN;

  const clearRevealedPasswords = () => {
    setRevealedPasswords({});
    setLoadingPasswordIds(new Set());
  };

  const handleCloseModal = () => {
    clearRevealedPasswords();
    onClose?.();
  };

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredUsers);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "users.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Users CSV downloaded.");
  };

  const handleTemplateDownload = async () => {
    try {
      await downloadEntityTemplate("users");
      showStatus("success", "Users template downloaded.");
    } catch (error) {
      showStatus("error", error?.response?.data?.message || "Template download failed.");
    }
  };

  const handleImportUsers = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    try {
      const result = await uploadEntityFile("users", file, { validateOnly: false });
      await refetch();
      showStatus("success", result?.message || "Users imported successfully.");
    } catch (error) {
      const payload = error?.response?.data;
      const details = Array.isArray(payload?.errors) ? payload.errors.slice(0, 5).join(" | ") : "";
      showStatus("error", `${payload?.message || "Users import failed."}${details ? ` ${details}` : ""}`);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleToggleDisabled = async (user, checked) => {
    try {
      await editUser({
        userId: user._id,
        isActive: !checked,
      }).unwrap();
      showStatus("success", "User status updated.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update account status.");
    }
  };

  const handleLoginAsUser = async (targetUserId) => {
    if (!userActions.canLoginAsUser) return;
    try {
      const currentAdminToken = localStorage.getItem("token");
      if (currentAdminToken) localStorage.setItem("adminFallbackToken", currentAdminToken);

      const response = await impersonateUser(targetUserId).unwrap();
      dispatch(adminApi.util.resetApiState());
      dispatch(loginSuccess({ user: response.user, token: response.token }));
      onClose?.();
      navigate("/dashboard", { replace: true, state: { __impersonate: true, t: Date.now() } });
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to login as user.");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId).unwrap();
      showStatus("success", "User deleted.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete user.");
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      await restoreUser(userId).unwrap();
      showStatus("success", "User restored.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to restore user.");
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialogUser?._id || !newPassword.trim()) {
      showStatus("warning", "New password required.");
      return;
    }
    if (newPassword !== confirmResetPassword) {
      showStatus("warning", "Passwords must match.");
      return;
    }

    try {
      await resetPassword({ userId: resetDialogUser._id, password: newPassword }).unwrap();
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[String(resetDialogUser._id)];
        return next;
      });
      showStatus("success", "Password reset successful.");
      setResetDialogUser(null);
      setNewPassword("");
      setConfirmResetPassword("");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reset password.");
    }
  };

  const closeForm = () => {
    setEditingUser(null);
    setModalMode("table");
  };

  const handleTogglePassword = async (userId) => {
    const targetId = String(userId || "");
    if (!targetId || !canRevealPasswords) return;

    if (Object.prototype.hasOwnProperty.call(revealedPasswords, targetId)) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      return;
    }

    setLoadingPasswordIds((prev) => new Set(prev).add(targetId));
    try {
      const response = await revealUserPassword(targetId).unwrap();
      setRevealedPasswords((prev) => ({
        ...prev,
        [targetId]: response?.password || "",
      }));
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reveal password.");
    } finally {
      setLoadingPasswordIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  };

  const renderPasswordCell = (user) => {
    const userId = String(user?._id || "");
    const loading = loadingPasswordIds.has(userId);
    const revealedPassword = revealedPasswords[userId];
    const isVisible = Object.prototype.hasOwnProperty.call(revealedPasswords, userId);

    if (!canRevealPasswords) {
      return <VisibilityOffIcon sx={{ fontSize: 18, color: "#9ca3af" }} />;
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontWeight: 700, minWidth: 74 }}>
          {isVisible ? revealedPassword : "••••••••"}
        </Typography>
        <IconButton
          type="button"
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleTogglePassword(userId);
          }}
          disabled={loading}
          sx={{ color: "#2563eb" }}
        >
          {loading ? (
            <CircularProgress size={16} />
          ) : isVisible ? (
            <VisibilityIcon sx={{ fontSize: 18 }} />
          ) : (
            <VisibilityOffIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Stack>
    );
  };

  const handleToggleAssignDevice = async (deviceRow, nextChecked) => {
    if (!assignDeviceDialogUser?._id || !deviceRow?.id) return;
    const deviceId = String(deviceRow.id);
    const userId = String(assignDeviceDialogUser._id);
    const fallbackOwnerId = String(currentUser?._id || currentUser?.id || "");
    const isPrimaryAssigned = String(deviceRow.assignedToUserId || "") === userId;
    const nextAssignedViewers = nextChecked
      ? Array.from(new Set([...(deviceRow.assignedToViewers || []), userId]))
      : (deviceRow.assignedToViewers || []).filter((id) => String(id) !== userId);

    setPendingAssignIds((prev) => new Set(prev).add(deviceId));
    try {
      const payload = {
        deviceId,
        assignedToViewers: nextAssignedViewers,
      };

      // Legacy exclusive assignments should be releasable from the same dialog.
      if (isPrimaryAssigned && !nextChecked) {
        payload.assignedToUser = null;
      }
      if (!nextChecked && String(deviceRow.adminOwnerId || "") === userId && fallbackOwnerId) {
        payload.adminId = fallbackOwnerId;
      }
      if (!nextChecked && String(deviceRow.dealerOwnerId || "") === userId) {
        payload.dealer = null;
      }

      await editDevice(payload).unwrap();

      setAssignDeviceCache((prev) => {
        const next = new Map(prev);
        const existing = next.get(deviceId) || {};
        next.set(deviceId, {
          ...existing,
          _id: existing?._id || deviceId,
          assignedToUser: isPrimaryAssigned && !nextChecked ? null : (existing?.assignedToUser ?? deviceRow.assignedToUserId ?? null),
          adminId:
            !nextChecked && String(deviceRow.adminOwnerId || "") === userId && fallbackOwnerId
              ? fallbackOwnerId
              : (existing?.adminId ?? deviceRow.adminOwnerId ?? null),
          dealer:
            !nextChecked && String(deviceRow.dealerOwnerId || "") === userId
              ? null
              : (existing?.dealer ?? deviceRow.dealerOwnerId ?? null),
          assignedToViewers: nextAssignedViewers,
        });
        return next;
      });
      setAssignOverrides((prev) => {
        const next = new Map(prev);
        next.set(deviceId, Boolean(nextChecked));
        return next;
      });
      await refetch();
      showStatus("success", nextChecked ? "Device assigned successfully." : "Device unassigned successfully.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Purchase token not found");
    } finally {
      setPendingAssignIds((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
    }
  };

  const handleAssignGroup = async () => {
    if (!assignGroupDialogUser?._id || !assignGroupTargetId) {
      showStatus("warning", "Please select an admin/dealer.");
      return;
    }

    try {
      await assignUserToGroupAdmin({
        groupAdminId: assignGroupTargetId,
        userIdToManage: assignGroupDialogUser._id,
      }).unwrap();
      showStatus("success", "User assignment updated successfully.");
      setAssignGroupDialogUser(null);
      setAssignGroupTargetId("");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to link user.");
    }
  };

  const resetAssignDeviceDialog = () => {
    setAssignDeviceDialogUser(null);
    setAssignDeviceSearch("");
    setPendingAssignIds(new Set());
    setAssignOverrides(new Map());
    setAssignDeviceCache(new Map());
  };

  const resetAssignGroupDialog = () => {
    setAssignGroupDialogUser(null);
    setAssignGroupTargetId("");
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "76vh" } }}>
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Customer</Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {modalMode === "table" ? (
            <>
              <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e5e7eb" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr auto" }, gap: 1.5, alignItems: "center" }}>
                  <TextField
                    placeholder="Search by name"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    sx={inputSx}
                    InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                  />
                  <TextField
                    placeholder="Search by Login Id"
                    value={searchLoginId}
                    onChange={(e) => setSearchLoginId(e.target.value)}
                    sx={inputSx}
                    InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                  />
                  <TextField
                    placeholder="Search by phone"
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    sx={inputSx}
                    InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton onClick={() => setModalMode("create")} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
                      <AddIcon />
                    </IconButton>
                    <IconButton onClick={() => fileInputRef.current?.click()} sx={{ color: "#22c55e" }}>
                      <UploadIcon />
                    </IconButton>
                    <IconButton onClick={handleTemplateDownload} sx={{ color: "#334155" }}>
                      <DownloadIcon />
                    </IconButton>
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleImportUsers} />
                  </Stack>
                </Box>
              </Box>

              <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 88 }}>
                  <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))} MenuProps={ROWS_PER_PAGE_MENU_PROPS}>
                    {CUSTOM_ROWS_PER_PAGE_OPTIONS.map((size) => (
                      <MenuItem key={size} value={size}>
                        {getRowsPerPageLabel(size)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Show All Customers</Typography>
                  <Switch checked={showAllCustomers} onChange={(e) => setShowAllCustomers(e.target.checked)} />
                  <IconButton onClick={() => refetch()} sx={{ color: "#6b7280" }}>
                    <RefreshIcon />
                  </IconButton>
                  <IconButton onClick={handleDownloadCsv} sx={{ color: "#6b7280" }}>
                    <DownloadIcon />
                  </IconButton>
                </Stack>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "56vh", borderRadius: 0 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {["", "Name", "Password", "User ID", "Mobile", "Created By", "Last Login", "Last Activity", "Disabled", "Actions"].map((head) => (
                        <TableCell key={head} sx={{ fontWeight: 700, color: "#4b5563", bgcolor: "#fff", borderBottom: "1px solid #d1d5db" }}>
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedUsers.length ? (
                      pagedUsers.map((user) => (
                        <TableRow key={user._id} hover>
                          <TableCell>□</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{user.username || "-"}</TableCell>
                          <TableCell>{renderPasswordCell(user)}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{user.uniqueId}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{user.mobileNo}</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{user.createdByName}</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{user.lastLoginLabel}</TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{user.lastActivityLabel}</TableCell>
                          <TableCell>
                            <Switch
                              size="small"
                              checked={!!user.disabled}
                              onChange={(event) => handleToggleDisabled(user, event.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.6}>
                              {userActions.canLoginAsUser && (
                                <Tooltip title="Login as User">
                                  <IconButton size="small" onClick={() => handleLoginAsUser(user._id)} sx={{ bgcolor: "#f8fafc", borderRadius: 1 }}>
                                    <LoginIcon sx={{ fontSize: 18, color: "#111827" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {!user.isDeleted && canAssignDevice && (
                                <Tooltip title="Assign Device">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setAssignDeviceDialogUser(user);
                                      setAssignDeviceSearch("");
                                      setPendingAssignIds(new Set());
                                      setAssignOverrides(new Map());
                                    }}
                                    sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                  >
                                    <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {!user.isDeleted && canAssignUserToGA && groupAdmins.length > 0 && (
                                <Tooltip title="Link User To Group">
                                  <IconButton
                                    size="small"
                                    onClick={() => setAssignGroupDialogUser(user)}
                                    sx={{ bgcolor: "#2563eb", borderRadius: 1 }}
                                  >
                                    <PersonAddAlt1OutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {!user.isDeleted && (
                                <Tooltip title="Notification Alerts">
                                  <IconButton
                                    size="small"
                                    onClick={() => setNotificationDialogUser(user)}
                                    sx={{ bgcolor: "#fbbf24", borderRadius: 1 }}
                                  >
                                    <NotificationsActiveOutlinedIcon sx={{ fontSize: 18, color: "#111827" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {userActions.canEdit && (
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => { setEditingUser(user); setModalMode("edit"); }} sx={{ bgcolor: "#2563eb", borderRadius: 1 }}>
                                    <EditIcon sx={{ fontSize: 18, color: "#fff" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {userActions.canResetPassword && (
                                <Tooltip title="Reset Password">
                                  <IconButton size="small" onClick={() => setResetDialogUser(user)} sx={{ bgcolor: "#facc15", borderRadius: 1 }}>
                                    <KeyIcon sx={{ fontSize: 18, color: "#111827" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {user.isDeleted
                                ? userActions.canRestore && (
                                    <Tooltip title="Restore">
                                      <IconButton size="small" onClick={() => handleRestoreUser(user._id)} sx={{ bgcolor: "#22c55e", borderRadius: 1 }}>
                                        <RestoreFromTrashOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
                                      </IconButton>
                                    </Tooltip>
                                  )
                                : userActions.canDelete && (
                                    <Tooltip title="Delete">
                                      <IconButton size="small" onClick={() => handleDeleteUser(user._id)} sx={{ bgcolor: "#ef4444", borderRadius: 1 }}>
                                        <DeleteIcon sx={{ fontSize: 18, color: "#fff" }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 6, color: "#6b7280" }}>
                          No users found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ color: "#374151" }}>
                  Showing {displayRange.from} to {displayRange.to} of {filteredUsers.length} entries
                </Typography>
                <Pagination page={page} count={Math.max(1, totalPages)} onChange={(_, value) => setPage(value)} shape="rounded" />
              </Box>
            </>
          ) : (
            <Box sx={{ p: 2 }}>
              <AddUserModal
                open
                mode={modalMode === "edit" ? "edit" : "create"}
                initialData={editingUser}
                onClose={closeForm}
                onSuccess={async () => {
                  await refetch();
                  closeForm();
                  showStatus("success", modalMode === "edit" ? "User updated." : "User created.");
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <BrandResetPasswordDialog
        open={Boolean(resetDialogUser)}
        onClose={() => { setResetDialogUser(null); setNewPassword(""); setConfirmResetPassword(""); }}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        confirmPassword={confirmResetPassword}
        setConfirmPassword={setConfirmResetPassword}
        onSubmit={handleResetPassword}
        loading={isResettingPassword}
        minLength={6}
      />

      <ManagedUserNotificationDialog
        open={Boolean(notificationDialogUser)}
        onClose={() => setNotificationDialogUser(null)}
        targetUser={notificationDialogUser}
        devices={safeDevices}
        showStatus={showStatus}
      />

      <Dialog
        open={Boolean(assignDeviceDialogUser)}
        onClose={resetAssignDeviceDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "72vh" } }}
      >
        <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
          <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 900 }}>Assign Device</Typography>
            <IconButton onClick={resetAssignDeviceDialog}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ px: 3, py: 2 }}>
            <Typography sx={{ color: "#6b7280", fontWeight: 800, fontSize: 15, mb: 1.5 }}>Selected Customer</Typography>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0, border: "1px solid #eef2f7" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Name", "Password", "User ID", "Mobile", "Disabled", "License Owner", "Available Tokens"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: 700, color: "#6b7280", bgcolor: "#f8fafc", whiteSpace: "nowrap" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.username || "-"}</TableCell>
                    <TableCell>{renderPasswordCell(assignDeviceDialogUser)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.uniqueId || "-"}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.mobileNo || "-"}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{assignDeviceDialogUser?.isActive === false ? "Disabled" : "Not Disabled"}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{selectedLicenseOwner?.username || "-"}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{selectedLicenseCount}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ px: 3, pb: 1.5 }}>
            <TextField
              placeholder="Search"
              value={assignDeviceSearch}
              onChange={(event) => setAssignDeviceSearch(event.target.value)}
              sx={{ ...inputSx, width: "100%" }}
              InputProps={{ endAdornment: <SearchIcon style={{ color: "#fb7185" }} /> }}
            />
          </Box>

          <Box sx={{ px: 3, pb: 1.5 }}>
            <Box sx={{ fontSize: 12, color: "#475569", bgcolor: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 1.5, px: 1.5, py: 1 }}>
              {`DEBUG lite=${assignDeviceDebug.liteCount} full=${assignDeviceDebug.fullCount} safe=${assignDeviceDebug.safeCount} cache=${assignDeviceDebug.cacheCount} visible=${assignDeviceDebug.visibleCount} query="${assignDeviceDebug.query}" user=${assignDeviceDebug.targetUserId}`}
            </Box>
          </Box>

          <Box sx={{ px: 3, pb: 2.5 }}>
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "46vh", borderRadius: 0, border: "1px solid #eef2f7" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48, bgcolor: "#fff" }} />
                    {["Vehicle No", "IMEI", "Status", "Last Update", "Sim No", "Model", "Icon"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: 800, color: "#111827", bgcolor: "#fff", whiteSpace: "nowrap" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignableDevices.length ? (
                    assignableDevices.map((device) => {
                      const pending = pendingAssignIds.has(String(device.id));
                      return (
                        <TableRow key={device.id} hover>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={device.checked}
                              disabled={pending}
                              onChange={(event) => handleToggleAssignDevice(device, event.target.checked)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.name}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.imei}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: String(device.status || "").toLowerCase().includes("stop") ? "#10b981" : String(device.status || "").toLowerCase().includes("running") ? "#10b981" : "#ef4444" }}>
                            {String(device.status || "").replace(/_/g, " ")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.lastUpdate}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.simNo}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.model}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{device.icon}</TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#6b7280" }}>
                        No devices found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={resetAssignDeviceDialog}
                sx={{
                  minWidth: 220,
                  bgcolor: "#6b7280",
                  "&:hover": { bgcolor: "#4b5563" },
                  fontWeight: 900,
                  borderRadius: 1.2,
                }}
              >
                Close
              </Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(assignGroupDialogUser)} onClose={resetAssignGroupDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add User to Group</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={2}>
            <Autocomplete
              options={groupAdmins}
              getOptionLabel={(user) => `${user?.username || user?.name || "Admin"}`}
              value={groupAdmins.find((user) => String(user?._id) === String(assignGroupTargetId)) || null}
              onChange={(_, value) => setAssignGroupTargetId(value?._id || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    currentUserRole === ROLES.OFFICE_EMPLOYEE
                      ? "Select Sub-Admin"
                      : currentUserRole === ROLES.SUB_ADMIN
                        ? "Select Dealer"
                        : "Select Admin/Dealer"
                  }
                  required
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAssignGroupDialog}>Cancel</Button>
          <Button onClick={handleAssignGroup} variant="contained" disabled={!assignGroupTargetId}>
            Link to Group
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserPortalModal;
